import axios from '@/lib/axios'
import { buildAdminRequestConfig } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  AppUpdate,
  AppUpdateCounts,
  AppUpdateIdInput,
  AppUpdateIndexFilters,
  AppUpdateIndexResponse,
  AppUpdateReadState,
  AppUpdatesViewedResponse,
  CreateAppUpdateInput,
  UpdateAppUpdateInput,
} from './app-updates.types'

/** Every app updates request is scoped to the organization whose What's New it reads or writes. */
export interface AppUpdateOrganizationScope {
  organizationId: number
}

/** Calls that act on one app update; the Laravel routes require its id. */
export interface AppUpdateTarget extends AppUpdateOrganizationScope {
  appUpdateId: AppUpdateIdInput
}

/**
 * The query key entries each fetcher reads. The scoped key builder always sets the organization; it
 * can't mark the other entries required.
 */
export type AppUpdateCountsKeyEntries = AppUpdateOrganizationScope

export interface AppUpdateIndexKeyEntries extends AppUpdateOrganizationScope {
  filters?: AppUpdateIndexFilters | undefined
  offset?: number | undefined
  page?: number | undefined
  pageSize?: number | undefined
}

export interface AppUpdateShowKeyEntries extends AppUpdateOrganizationScope {
  appUpdateId?: number
}

/** Query functions read only the key. */
type AppUpdateQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'>

export async function getAppUpdates({
  queryKey: [{ organizationId, filters = {}, offset = 0, page = 1, pageSize = 20 }],
}: AppUpdateQueryContext<AppUpdateIndexKeyEntries>) {
  const params = {
    ...filters,
    offset,
    page,
    page_size: pageSize,
  }

  return axios.get<AppUpdateIndexResponse>(
    '/app-updates',
    buildAdminRequestConfig({ organizationId, params })
  )
}

export async function getAppUpdate({
  queryKey: [{ organizationId, appUpdateId }],
}: AppUpdateQueryContext<AppUpdateShowKeyEntries>) {
  return axios.get<AppUpdate>(
    '/app-updates/' + appUpdateId,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getAppUpdateCounts({
  queryKey: [{ organizationId }],
}: AppUpdateQueryContext<AppUpdateCountsKeyEntries>) {
  return axios.get<AppUpdateCounts>(
    '/app-updates/counts',
    buildAdminRequestConfig({ organizationId })
  )
}

export async function markAppUpdatesViewed({ organizationId }: AppUpdateOrganizationScope) {
  return axios.post<AppUpdatesViewedResponse>(
    '/app-updates/view',
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function markAppUpdateRead({ organizationId, appUpdateId }: AppUpdateTarget) {
  return axios.put<AppUpdateReadState>(
    '/app-updates/' + appUpdateId + '/read',
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function markAppUpdateUnread({ organizationId, appUpdateId }: AppUpdateTarget) {
  return axios.put<AppUpdateReadState>(
    '/app-updates/' + appUpdateId + '/unread',
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function createAppUpdate({
  organizationId,
  values,
}: AppUpdateOrganizationScope & { values: CreateAppUpdateInput }) {
  return axios.post<AppUpdate>('/app-updates', values, buildAdminRequestConfig({ organizationId }))
}

export async function updateAppUpdate({
  organizationId,
  appUpdateId,
  values,
}: AppUpdateTarget & { values: UpdateAppUpdateInput }) {
  return axios.put<AppUpdate>(
    '/app-updates/' + appUpdateId,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

/** The API answers 204 with no body. */
export async function deleteAppUpdate({ organizationId, appUpdateId }: AppUpdateTarget) {
  return axios.delete<void>(
    '/app-updates/' + appUpdateId,
    buildAdminRequestConfig({ organizationId })
  )
}
